import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import '@github/relative-time-element';
import { __ } from '@wordpress/i18n';

// store
import { queryKeys } from '../../../../lib/query-client';
import { useCommCenter } from '../../context';

// service
import CommunicationsService from '../../../../service/communications-service';

// comm center
import { toReply } from '../../adapters';

/**
 * Expanded thread of a comment: the replies plus the reply form.
 *
 * The thread is read from `GET /communications/{id}/replies` the first time it
 * is opened; it shares the communications cache key, so posting a reply — which
 * invalidates that key — refreshes both the list and the counter.
 */
const CommentReplies = ({ comment }) => {
    const { addReply, can, pending, projectId } = useCommCenter();
    const [draft, setDraft] = useState('');

    const { data, isLoading, isError } = useQuery({
        queryKey: [...queryKeys.project(projectId), 'communications', comment.id, 'replies'],
        queryFn: () => CommunicationsService.listRepliesAsync(comment.id),
        enabled: Boolean(comment.repliesCount),
    });

    const replies = (data?.items || []).map(toReply);

    const submit = async () => {
        if (!draft.trim()) return;

        await addReply(comment.id, draft);
        setDraft('');
    }

    return (
        <section className="commentReplies">
            {Boolean(comment.repliesCount) && isLoading && (
                <p className="commCenter__empty">{__('Carico le risposte…', 'soundbetween')}</p>
            )}

            {isError && (
                <p className="commCenter__empty">
                    {__('Non è stato possibile caricare le risposte.', 'soundbetween')}
                </p>
            )}

            {Boolean(replies.length) && (
                <ul className="commentReplies__list">
                    {replies.map(reply => (
                        <li key={reply.id}>
                            <p className="commentCard__author">
                                <span className="commentCard__authorName">{reply.author.name}</span>
                                {reply.author.role && (
                                    <span className="commentCard__authorRole">{` | ${reply.author.role}`}</span>
                                )}
                            </p>
                            <p className="commentReplies__text">{reply.text}</p>
                            <relative-time class="commentCard__time" datetime={reply.createdAt}/>
                        </li>
                    ))}
                </ul>
            )}

            {can.comment && (
                <div className="commentReplies__form">
                    <textarea
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        placeholder={__('Scrivi una risposta…', 'soundbetween')}
                        aria-label={__('Scrivi una risposta', 'soundbetween')}/>
                    <button
                        type="button"
                        className="commButton commButton--accent"
                        disabled={!draft.trim() || pending?.comment}
                        onClick={submit}>
                        {pending?.comment
                            ? __('Invio…', 'soundbetween')
                            : __('Rispondi', 'soundbetween')}
                    </button>
                </div>
            )}
        </section>
    )
}

export default CommentReplies;
